/**
 * Signed-URL lifetime and refresh timings, in one dependency-free module.
 *
 * These four numbers only make sense relative to each other, and two of them
 * are needed on the client. `convex/media/bucket.ts` cannot serve that: it
 * imports the R2 client, so importing it from a React hook would pull a server
 * SDK into the browser bundle. Duplicating the numbers instead is worse — drift
 * is invisible at runtime (a client threshold above the TTL refreshes on every
 * render; one below the server margin never receives a fresh URL), so they live
 * here, imported by both sides.
 *
 * Deliberately imports nothing.
 */

/**
 * Signed-URL TTL for private objects, in seconds.
 *
 * This value **is the revocation latency** for already-minted URLs: revoking
 * ACL `read` stops new mints immediately, but a URL already in a browser,
 * proxy or log stays usable until it expires. That is why it is 5 minutes and
 * why the override is bounded by the same 5 minutes rather than by R2's 7-day
 * signing maximum — a deployment cannot widen its own revocation window.
 *
 * `PRIVATE_R2_SIGNED_URL_TTL_SECONDS` therefore only ever *lowers* the window;
 * a larger value is clamped, not honored. Long-lived signed URLs handed to
 * external generation providers are a separate, explicitly-passed TTL
 * (`PROVIDER_REFERENCE_URL_TTL_SECONDS`), not this default.
 */
export const MAX_PRIVATE_SIGNED_URL_TTL_SECONDS = 300;

/**
 * Floor for the same override, in seconds.
 *
 * The refresh machinery treats a URL with less than
 * {@link CLIENT_REFRESH_THRESHOLD_MS} (120s) of life left as needing a refresh,
 * and the refresh mutation re-signs at that same boundary. A TTL at or under
 * that threshold is self-defeating: every URL is born stale, so the resolver
 * re-signs on every read and the client refreshes on every render — more
 * signing load, no revocation benefit, and media that flickers.
 *
 * 180s is the smallest value that leaves a full minute of usable life after the
 * client's own refresh threshold, which is the headroom a refresh round trip
 * needs. Lowering the window further is a real request, but it has to move the
 * thresholds too, so it is clamped here rather than degrading silently.
 */
export const MIN_PRIVATE_SIGNED_URL_TTL_SECONDS = 180;

/**
 * Read-path freshness margin: `storage.resolver.resolveStorageUrl` (a **query**,
 * which cannot sign) hands back the cached signed URL while it still has more
 * than this many ms of life, and otherwise reports `refreshRequired` so the
 * client calls the refresh **mutation**.
 *
 * This is the margin for *reusing* a cached URL on a read, not the boundary at
 * which a new one is minted — that is
 * {@link REFRESH_MINT_THRESHOLD_MS}. Must stay below
 * {@link MIN_PRIVATE_SIGNED_URL_TTL_SECONDS}: a margin at or above the TTL
 * would make every freshly-signed URL instantly "stale", so the resolver would
 * report `refreshRequired` on every single read.
 */
export const SERVER_FRESHNESS_MARGIN_MS = 60 * 1000;

/**
 * Client refresh threshold: the client asks for a refresh — on render, and on a
 * timer armed against the URL's own expiry — when a signed URL has less than
 * this many ms of life remaining.
 *
 * Kept below {@link MIN_PRIVATE_SIGNED_URL_TTL_SECONDS} so a freshly-signed URL
 * is not immediately due for refresh, and at or above
 * {@link SERVER_FRESHNESS_MARGIN_MS} so the read path never reports
 * `refreshRequired` for a URL the client still considers fresh (it would ask on
 * every render).
 *
 * It is deliberately the *same* number as {@link REFRESH_MINT_THRESHOLD_MS}:
 * that is what makes a client-initiated refresh actually produce a new URL.
 */
export const CLIENT_REFRESH_THRESHOLD_MS = 2 * 60 * 1000;

/**
 * Re-sign boundary for `storage.resolver.refreshStorageUrl`: the mutation mints
 * a new signed URL unless the cached one still has more than this many ms left.
 *
 * **This is the constant the whole refresh handshake turns on.** The mutation is
 * the only path that can sign, so it must be willing to re-sign at *every*
 * threshold at which somebody asks — otherwise a caller that asks at its own
 * threshold gets the same near-expiry URL handed straight back, observes an
 * unchanged expiry, and (for a long-mounted component whose next timer is armed
 * off that expiry) never asks again before the URL dies.
 *
 * The two askers are the read path at {@link SERVER_FRESHNESS_MARGIN_MS} and the
 * client at {@link CLIENT_REFRESH_THRESHOLD_MS}, so this is the larger of the
 * two. Making it *narrower* than either is the bug; making it wider only costs
 * signatures.
 */
export const REFRESH_MINT_THRESHOLD_MS = Math.max(
  CLIENT_REFRESH_THRESHOLD_MS,
  SERVER_FRESHNESS_MARGIN_MS
);
